<template>
    <Modal
        ref="modal"
        class="generate-screen"
        :class="viewport.is"
        :width="viewport.is.mobile ? '100%' : '504px'"
        :full="viewport.is.mobile"
        :message="message"
        body-overflow
        teleport
        @close="$emit('close')"
    >
        <template #header>
            <Txt size="s" heading>
                <Icon name="edit-magic" size="32" right />
                Generate screen
            </Txt>
        </template>
        <form
            id="question"
            @submit.prevent="submit"
        >
            <Field
                v-model:value="keywords"
                type="textarea"
                placeholder="Describe your screen's content and purpose, or leave this field blank to use reference screens only."
                :rows="4"
                show-info-always
            >
                <Txt size="xxs" heading>
                    Description
                </Txt>
            </Field>
            <Txt size="xxs" heading>
                Choose reference screens
            </Txt>
            <Txt size="s">
                Select existing screens you want to use as a reference.
            </Txt>
            <Tooltip
                position="top"
                :disabled="!!availableScreens.length"
                multiline
            >
                <Dropdown
                    type="select"
                    multiselect
                    full
                    :disabled="!availableScreens.length"
                >
                    <Txt size="xxxs" heading flex>
                        <Icon name="resource-library" class="anchor" right />
                        {{ selectedScreens.length === availableScreens.length && availableScreens.length ? 'All screens' : (selectedScreens.length === 1 ? '1 screen' : selectedScreens.length === 0 ? 'None' : selectedScreens.length + ' screens') }}
                    </Txt>
                    <template #menu>
                        <Tooltip
                            v-for="s in course.screens"
                            :key="s.sequence"
                            :disabled="!unsupportedScreenTypes.includes(s.type.name)"
                            position="top"
                        >
                            <Button
                                colour="white"
                                class="checkbox"
                                :active="selectedScreens.includes(s.sequence)"
                                :disabled="!availableScreens.includes(s)"
                                @click="selectScreen(s.sequence)"
                            >
                                <div class="screen-option">
                                    <Icon
                                        :name="s.type.icon"
                                        type="screen"
                                        class="active"
                                        right
                                    />
                                    <Txt size="xxxs" heading :clamp="1">
                                        {{ s.sequence }}: {{ s.title || s.type.title }}
                                    </Txt>
                                </div>
                                <Checkbox
                                    :value="selectedScreens.includes(s.sequence)"
                                    @click="selectScreen(s.sequence)"
                                />
                            </Button>
                            <template #tooltip>
                                <Txt size="xs">
                                    You cannot base a screen on this screen type.
                                </Txt>
                            </template>
                        </Tooltip>
                    </template>
                </Dropdown>
                <template #tooltip>
                    <Txt size="s">
                        1 or more screens required to use this option.
                    </Txt>
                </template>
            </Tooltip>
        </form>
        <template #footer>
            <Button
                type="submit"
                form="question"
                size="xxs" heading
                colour="smoke"
                active-colour="primary"
                :disabled="!keywords && !selectedScreens.length"
                :status="status"
            >
                Generate
            </Button>
        </template>
    </Modal>
</template>

<script>
import { url } from '@/helpers/utilities';
import { tileToString } from '@/helpers/tile';
import { useResponsive } from '@/store/responsive';

import Txt from '@/components/form/txt';
import Field from '@/components/form/field';
import Icon from '@/components/utility/icon';
import Modal from '@/components/utility/modal';
import Button from '@/components/utility/button';
import Checkbox from '@/components/form/checkbox';
import Tooltip from '@/components/utility/tooltip';
import Dropdown from '@/components/utility/dropdown';

export default {
    name: 'GenerateScreen',
    components: { Txt, Field, Icon, Button, Checkbox, Tooltip, Modal, Dropdown },
    props: {
        screen: { type: Object, default: null },
        course: { type: Object, default: null }
    },
    emits: ['close', 'submit'],
    data() {
        return {
            screens: [ ...this.course.screens ],
            selectedScreens: [],
            keywords: '',

            questionCount: 0,

            status: null,
            viewport: useResponsive()
        };
    },
    computed: {
        unsupportedScreenTypes() {
            return ['embed', 'document', 'slideshow', 'video', 'quiz'];
        },
        availableScreens() {
            return this.course.screens.filter(screen => {
                if (this.unsupportedScreenTypes.includes(screen.type.name)) return false;
                if (screen.id && screen.id === this.screen.id) return false;
                if (screen.tempId && screen.tempId === this.screen.tempId) return false;
                return true;
            });
        },
        message() {
            if (!this.keywords || !this.selectedScreens.length) return;
            return {
                type: 'info',
                text: 'When generating screens we favor the description over reference screens.'
            };
        }
    },
    methods: {
        url,
        selectScreen(sequence) {
            if (this.selectedScreens.includes(sequence)) {
                this.selectedScreens = this.selectedScreens.filter(screen => screen !== sequence);
            } else {
                this.selectedScreens.push(sequence);
            }
        },
        submit() {
            const prompt = this.keywords;
            let content = null;
            if (this.selectedScreens.length) {
                content = this.selectedScreens.map(sequence => {
                    const screen = this.course.screens.find(screen => screen.sequence === sequence);
                    return tileToString(screen);
                }).join('\r\n');
            }

            this.$emit('submit', { prompt, content, count: Math.max(this.selectedScreens.length, 2) });

            this.$track.event('Screen Generated', {
                'context': 'from scratch',
                'ai_feed': this.keywords,
                'all_screens_used': this.selectedScreens.length === this.availableScreens.length,
                'number_of_screens': this.course.screens.length,
                'number_of_ai_supported_screens': this.availableScreens.length,
                'number_of_screens_used': this.selectedScreens.length,
            });

            this.$refs.modal.close();
        },
        open() {
            this.$refs.modal.open();
        },
    }
};
</script>

<style lang="less">
.modal.generate-screen {
    .header {
        .txt-component.heading svg { margin-right: 4px; }
    }
    .dropdown .popup .popup-inner {
        .cobutton {
            width: 100%;
            justify-content: space-between;
            &.disabled { opacity: 0.5; }
        }
    }
    .modal-inner > .body {
        height: 504px;
    }
    form {
        .tooltip {
            width: 100%;
            .dropdown { flex: 1; }
        }
        > .txt-component.heading { padding: 2px 0 4px; }
        > .txt-component.body { padding: 0 0 10px; }
    }
    .screen-option {
        flex: 1;
        position: relative;
        display: flex;
        align-items: center;
        &:hover {
            .popup { opacity: 1; }
        }
        .popup {
            opacity: 0; 
            transition: var(--smooth);
            &.show { opacity: 1; }
            .popup-inner {
                padding: 0;
                flex-direction: column;
                box-shadow: var(--shadow-lg);
                .screen-preview {
                    position: static;
                    width: 190px;
                    aspect-ratio: 1.4;
                    border-radius: 8px;
                    outline: 1.5px solid var(--primary);
                }
            }
        }
    }
}
</style>
